import React, { useState } from 'react';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { Grid, Typography, TextField, Button } from '@mui/material';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import './UpdatePassword.scss';

interface UpdatePasswordProps {
    open: boolean;
    onCancel: () => void;
    onSubmit: (newPassword: string) => Promise<void>;
}

export default function UpdatePassword({ open, onCancel, onSubmit }: UpdatePasswordProps): JSX.Element {
    const { showToast } = useToastContext();
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [newPasswordError, setNewPasswordError] = useState<string>('');
    const [confirmPasswordError, setConfirmPasswordError] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const allowedSpecialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?/~';
    const passwordRegex = new RegExp(
        '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-=[\\]{}|;:,.<>?/~])[A-Za-z\\d!@#$%^&*()_+\\-=[\\]{}|;:,.<>?/~]{8,}$'
    );

    const validatePassword = (password: string): string => {
        if (!passwordRegex.test(password)) {
            return 'Password must contain at least 1 lowercase, 1 uppercase, 1 digit, and 1 allowed special character.';
        }
        // Check for disallowed special characters
        const specialChars = password.replace(/[A-Za-z0-9]/g, '');
        for (const char of specialChars) {
            if (!allowedSpecialChars.includes(char)) {
                return `Special character "${char}" is not allowed.`;
            }
        }
        return '';
    };

    const handlePasswordChange = (value: string) => {
        setNewPassword(value);
        // Required check
        if (!value || value.trim() === '') {
            setNewPasswordError('New password is required.');
            return;
        }
        const validation = validatePassword(value);
        setNewPasswordError(validation);
        // re-validate confirm
        if (confirmPassword && value !== confirmPassword) {
            setConfirmPasswordError('Passwords do not match.');
        } else if (!confirmPassword) {
            setConfirmPasswordError('');
        } else {
            setConfirmPasswordError('');
        }
    };

    const handleConfirmPasswordChange = (value: string) => {
        setConfirmPassword(value);
        if (!value || value.trim() === '') {
            setConfirmPasswordError('Confirm password is required.');
            return;
        }
        if (newPassword !== value) {
            setConfirmPasswordError('Passwords do not match.');
        } else {
            setConfirmPasswordError('');
        }
    };

    const handleSubmit = async (): Promise<void> => {
        // Required checks
        if (!newPassword || newPassword.trim() === '') {
            setNewPasswordError('New password is required.');
            return;
        }
        if (!confirmPassword || confirmPassword.trim() === '') {
            setConfirmPasswordError('Confirm password is required.');
            return;
        }

        // Validation
        const validation = validatePassword(newPassword);
        if (validation) {
            setNewPasswordError(validation);
            return;
        }

        // Match
        if (newPassword !== confirmPassword) {
            setConfirmPasswordError('Passwords do not match.');
            return;
        }

        setNewPasswordError('');
        setConfirmPasswordError('');

        try {
            setIsLoading(true);
            await onSubmit(newPassword);
            showToast('success', 'Password changed successfully.');
            onCancel();
        } catch (err) {
            showToast('error', 'Failed to change password. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={open}>
            <DialogContent className="password-update-dialog">
                <Grid container justifyContent="center">
                    <Grid item xs={12} sx={{ height: '100%' }}>
                        <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                            direction="row"
                            sx={{ mb: 2 }}
                        >
                            <Grid item>
                                <Typography variant="h6" gutterBottom>
                                    Update Password
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                    You must update your password before continuing.
                                </Typography>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} mt={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="New Password"
                                    fullWidth
                                    type="password"
                                    value={newPassword}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        handlePasswordChange(e.target.value)
                                    }
                                    error={Boolean(newPasswordError)}
                                    helperText={newPasswordError}
                                    disabled={isLoading}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Confirm New Password"
                                    fullWidth
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        handleConfirmPasswordChange(e.target.value)
                                    }
                                    error={Boolean(confirmPasswordError)}
                                    helperText={confirmPasswordError}
                                    disabled={isLoading}
                                />
                            </Grid>
                            <Grid item xs={12} container justifyContent="flex-end" spacing={1}>
                                <Grid item>
                                    <Button
                                        variant="contained"
                                        sx={{ backgroundColor: '#446388' }}
                                        onClick={handleSubmit}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Updating...' : 'Update Password'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
}